import React, { ReactElement } from 'react';
import { redirect } from '../../../helper/redirectHelper';
import { Container, Title } from './PatentOntologyOverviewPage.styled';

import patentOverviewDiagram from './static/patent-ontology-overview-diagram.png';
import Terms from '../../components/common/Terms';
const PatentOntologyOverviewPage = (): ReactElement => {
    return (
        <Container className="container">
            <Title data-testid="title">Patent Ontology Overview</Title>
            <p>
                This Linked Open EP Data service uses an
                <a href="https://www.w3.org/TR/owl2-overview/"> OWL</a> ontology to describe a conceptual model of
                patents and the structure of the patent data. OWL is a formal ontology language standardized by the
                World Wide Web Consortium (W3C).
            </p>
            <p>
                The prefix normally used for terms in the patent ontology is
                <span className="code"> patent:</span>. The ontology namespace is
                <a href={redirect('def/patent')}> https://data.epo.org/def/patent/</a>. The URLs identifying
                all terms in the ontology dereference and are also described in the{' '}
                <a href="/linked-data/documentation/patent-ontology-reference">patent ontology reference page</a>.
            </p>
            <p>An overview of the major classes in the patent ontology is given in the following diagram:</p>
            <div>
                <img src={patentOverviewDiagram} alt="overview of patent ontology" />
            </div>
            <p>
                The central concepts in the ontology are the classes
                <a href={redirect('def/patent/Application')}> patent:Application</a> and
                <a href={redirect('def/patent/Publication')}> patent:Publication</a>. An application is an application
                for a patent or other intellectual property right. The property{' '}
                <a href={redirect('def/patent/publication')}>patent:publication</a> relates an application to the
                documents published as part of the formal process of processing the application.
            </p>
            <p>
                Both applications and publications are related to a patent authority, Applications are related to the
                authority to which the application was made by the property
                <a href={redirect('def/patent/applicationAuthority')}> patent:applicationAuthority</a>. Publications are
                related to the authority which published the document by the property{' '}
                <a href={redirect('def/patent/publicationAuthority')}>patent:publicationAuthority</a>. Patent
                authorities may be national patent authorities such as the United Kingdom{' '}
                <a href="https://www.gov.uk/government/organisations/intellectual-property-office">
                    Intellectual Property Office
                </a>{' '}
                or an international authority such as the <a href="https://www.epo.org">European Patent Office</a>.
            </p>
            <p>
                An application may be a member of a{' '}
                <a href={redirect('def/patent/SimpleFamily')}>patent:SimpleFamily</a>. A simple family is a family of
                related applications such as applications concerning the same invention in different countries. The
                properties
                <a href={redirect('def/patent/familyMember')}> patent:familyMember</a> and
                <a href={redirect('def/patent/familyMemberOf')}> patent:familyMemberOf</a> relate a family to its
                members and an application to its simple family respectively.
            </p>
            <p>
                To aid finding them, applications are classified using the{' '}
                <a href="https://www.cooperativepatentclassification.org/about.html">
                    Cooperative Patent Classification
                </a>
                (CPC) classification scheme. (As described below, publications are also classified by the older
                <a href="https://www.wipo.int/classifications/ipc/en/"> International Patent Classification</a> (IPC)
                scheme which CPC extends.)
            </p>
            <p>
                The properties
                <a href={redirect('def/patent/classificationCPCInventive')}> patent:classificationCPCInventive </a>
                and
                <a href={redirect('def/patent/classificationCPCAdditional')}> patent:classificationCPCAdditional</a>
                relate an application to its CPC classifications. The 'inventive' properties indicate the core
                classifications of the invention. The 'additional' properties represent additional classifications to
                which the invention may be related.
            </p>
            <p>
                Publications have properties <a href={redirect('def/patent/inventorVC')}>patent:inventorVC</a>,
                <a href={redirect('def/patent/applicantVC')}> patent:applicantVC</a> and
                <a href={redirect('def/patent/agentVC')}> patent:agentVC</a> which relate it to
                <a href="https://www.w3.org/TR/vcard-rdf/"> VCards</a> (virtual business cards) for each of the
                invention's inventors, the applicant and each of the agents of the applicant respectively.
            </p>
            <p>
                Publications are related to patent publications that they cite by the property
                <a href={redirect('def/patent/citesPatentPublication')}> patent:citesPatentPublication</a>. A
                publication may also cite non patent literature such as scientific publications. Such citations are
                represented by an instance of the class
                <a href={redirect('def/patent/CitationNPL')}> CitationNPL</a>. The property
                <a href={redirect('def/patent/CitationNPL')}> citationNPL</a> relates a publication to a citation of non
                patent literature.
            </p>
            <p>
                Publications are classified using the{' '}
                <a href="https://www.wipo.int/classifications/ipc/en/">International Patent Classification scheme</a>{' '}
                (IPC). The properties
                <a href={redirect('def/patent/classificationIPCInventive')}> ipc:classificationInventive</a>
                and
                <a href={redirect('def/patent/classificationIPCAdditional')}> cpc:classificationAdditional </a>
                relate an application to its IPC classifications. As with CPC, the 'inventive' properties indicate the
                core classifications of the invention and the 'additional' properties represent additional
                classifications to which the invention may be related.
            </p>
            <p>
                Publications are also related to representations of the publication by the property{' '}
                <a href="def/patent/representation">representation</a>. A Representation is a
                representation of the publication in some format such as HTML, XML or PDF.
            </p>
            <Terms />
        </Container>
    );
};
export default PatentOntologyOverviewPage;